import React, { useState, useEffect, useRef } from 'react';
import styles from './Navbar.module.css';
import { auth } from '../../config/firebase';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import NotificationBell from '../NotificationPanel/NotificationBell.jsx';
import { watchAdminStatus } from '../../utils/adminAuth';

const NAV_ITEMS = [
  { label: 'Admin', path: '/admin', adminOnly: true },
  { label: 'Home', path: '/' },
  { label: 'Dictionary', path: '/dictionary' },
  { label: 'Courses', path: '/learn' },
  { label: 'Community', path: '/community' },
  { label: 'Index', path: '/dictionary/words' },
  { label: 'About', path: '/about' },
];

const MORE_ITEMS = [
  { label: 'Folk Song Lyrics', path: '/songs' },
  { label: 'Contribute Lyrics', path: '/contribute-lyrics' },
  { label: 'Literature', path: '/literature' },
  { label: 'Grammar', path: '/grammar' },
  { label: 'Quiz', path: '/quiz' },
];

function NavigationBar() {
  const [isActive, setIsActive] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [user, setUser] = useState(null);
  const moreDropdownRef = useRef(null);
  const mobileMoreDropdownRef = useRef(null);
  const moreCloseTimerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 960);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsActive(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideDesktop = moreDropdownRef.current && moreDropdownRef.current.contains(event.target);
      const isInsideMobile = mobileMoreDropdownRef.current && mobileMoreDropdownRef.current.contains(event.target);

      if (!isInsideDesktop && !isInsideMobile) {
        setShowMoreDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (moreCloseTimerRef.current) {
        clearTimeout(moreCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const unsubscribe = watchAdminStatus(auth, ({ user: nextUser, isAdmin: nextIsAdmin }) => {
      setIsSignedIn(!!nextUser);
      setUser(nextUser || null);
      setIsAdmin(nextIsAdmin);
    });

    return () => unsubscribe();
  }, []);

  const logOut = async () => {
    try {
      await signOut(auth);
      navigate('/auth');
    } catch (error) {
      console.error(error);
    }
  };

  const toggleActiveClass = () => {
    setIsActive((prev) => {
      const next = !prev;
      if (!next) setShowMoreDropdown(false);
      return next;
    });
  };

  const handleNavClick = (path) => () => {
    navigate(path);
    setIsActive(false);
    setShowMoreDropdown(false);
  };

  const handleMoreItemClick = (path) => () => {
    navigate(path);
    setShowMoreDropdown(false);
    setIsActive(false);
  };

  const handleBrandNavigate = () => {
    navigate('/');
  };

  const handleBrandKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleBrandNavigate();
    }
  };

  const visibleNavItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  const openMoreDropdown = () => {
    if (moreCloseTimerRef.current) clearTimeout(moreCloseTimerRef.current);
    setShowMoreDropdown(true);
  };

  const closeMoreDropdown = (delay = 90) => {
    if (moreCloseTimerRef.current) clearTimeout(moreCloseTimerRef.current);
    moreCloseTimerRef.current = setTimeout(() => setShowMoreDropdown(false), delay);
  };

  return (
    <header className={styles.headerShell}>
      <nav className={styles.navbar}>
        <div className={styles.brandGroup}>
          <div
            className={styles.brandIdentity}
            role="link"
            tabIndex={0}
            onClick={handleBrandNavigate}
            onKeyDown={handleBrandKeyDown}
            aria-label="Go to home"
          >
            <img src="/logo.png" alt="PahadiLingo" className={styles.logo} width="40" height="40" />
            <span className={styles.brandLabelGroup}>
              <span className={styles.brandName}>PahadiLingo</span>
              <span className={styles.brandSubtitle}>Kumaoni Dictionary</span>
            </span>
          </div>
        </div>

        <div className={styles.menuCluster}>
          <ul className={`${styles.navMenu} ${isActive ? styles.active : ''}`}>
            {visibleNavItems.map((item) => (
              <li key={item.label}>
                <button
                  type="button"
                  className={styles.navLink}
                  onClick={handleNavClick(item.path)}
                >
                  {item.label}
                </button>
              </li>
            ))}

            <li
              className={styles.dropdownContainer}
              ref={moreDropdownRef}
              onMouseEnter={() => {
                if (!isMobile) openMoreDropdown();
              }}
              onMouseLeave={() => {
                if (!isMobile) closeMoreDropdown(120);
              }}
            >
              <button
                type="button"
                className={`${styles.navLink} ${styles.dropdownToggle}`}
                onClick={() => setShowMoreDropdown((prev) => !prev)}
                aria-label="More options"
                aria-expanded={showMoreDropdown}
              >
                More {showMoreDropdown ? <ExpandLessRoundedIcon sx={{ fontSize: 18, ml: 0.5 }} /> : <ExpandMoreRoundedIcon sx={{ fontSize: 18, ml: 0.5 }} />}
              </button>
              {showMoreDropdown && (
                <ul className={styles.dropdownMenu}>
                  {MORE_ITEMS.map((item) => (
                    <li key={item.label}>
                      <button
                        type="button"
                        className={styles.dropdownLink}
                        onClick={handleMoreItemClick(item.path)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          </ul>

          <div className={styles.authContainer}>
            {user && <NotificationBell userId={user.uid} />}
            <button
              type="button"
              className={styles.authButton}
              onClick={isSignedIn ? logOut : () => navigate('/auth')}
            >
              {isSignedIn ? 'Log Out' : 'Sign In'}
            </button>
          </div>

          <button
            type="button"
            className={`${styles.hamburger} ${isActive ? styles.active : ''}`}
            onClick={toggleActiveClass}
            aria-label={isActive ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isActive}
          >
            {isActive ? (
              <CloseRoundedIcon className={styles.hamburgerIcon} />
            ) : (
              <MenuRoundedIcon className={styles.hamburgerIcon} />
            )}
          </button>
        </div>
      </nav>

      {isMobile && isActive && (
        <div className={styles.mobilePanel}>
          <ul className={styles.mobileMenu}>
            {visibleNavItems.map((item) => (
              <li key={item.label}>
                <button
                  type="button"
                  className={styles.mobileLink}
                  onClick={handleNavClick(item.path)}
                >
                  {item.label}
                </button>
              </li>
            ))}

            <li className={styles.mobileDropdownContainer} ref={mobileMoreDropdownRef}>
              <button
                type="button"
                className={`${styles.mobileLink} ${styles.mobileDropdownToggle}`}
                onClick={() => setShowMoreDropdown((prev) => !prev)}
                aria-label="More options"
                aria-expanded={showMoreDropdown}
              >
                More {showMoreDropdown ? <ExpandLessRoundedIcon sx={{ fontSize: 18, ml: 0.5 }} /> : <ExpandMoreRoundedIcon sx={{ fontSize: 18, ml: 0.5 }} />}
              </button>
              {showMoreDropdown && (
                <ul className={styles.mobileDropdownMenu}>
                  {MORE_ITEMS.map((item) => (
                    <li key={item.label}>
                      <button
                        type="button"
                        className={styles.mobileDropdownLink}
                        onClick={handleMoreItemClick(item.path)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>

            <li>
              <button
                type="button"
                className={`${styles.authButton} ${styles.mobileAuth}`}
                onClick={isSignedIn ? logOut : () => navigate('/auth')}
              >
                {isSignedIn ? 'Log Out' : 'Sign In'}
              </button>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}

export default NavigationBar;
